import { normalizePathForComparison, isDashboardPath } from '../jsx/layouts/nav/moduleNav';

/**
 * Navbar search is shown on dashboard (global) and module list pages.
 * Results always appear in the header dropdown — never by filtering the page.
 */
const MODULE_LIST_MATCHERS = [
  {
    label: 'Dashboard',
    match: (p) => p === '/task/tasklist',
    resultsScope: 'tasks',
    placeholder: 'Search tasks...',
  },
  {
    label: 'Worksheet',
    match: (p) => p === '/task/worksheet',
    resultsScope: 'worksheet',
    placeholder: 'Search worksheet tasks...',
  },
  {
    label: 'Projects',
    match: (p) =>
      p === '/task/projectlist' ||
      p === '/task/projecttasklist' ||
      p === '/task/projectdashboard',
    resultsScope: 'projects',
    placeholder: 'Search projects and project tasks...',
  },
  {
    label: 'Forum',
    match: (p) => p === '/forum/list' || p === '/forum/dashboard',
    resultsScope: 'forum',
    placeholder: 'Search discussions...',
  },
  {
    label: 'Documents',
    match: (p) => p === '/documents/dashboard',
    resultsScope: 'documents',
    placeholder: 'Search documents...',
  },
  {
    label: 'Documents',
    match: (p) => p === '/documents/mydocuments' || p.startsWith('/documents/folder/'),
    resultsScope: 'documents',
    placeholder: 'Search documents...',
  },
  {
    label: 'Documents',
    match: (p) => p === '/documents/shared',
    resultsScope: 'documents-shared',
    placeholder: 'Search shared documents...',
  },
  {
    label: 'Documents',
    match: (p) => p === '/documents/favorites',
    resultsScope: 'documents-favorites',
    placeholder: 'Search favorite documents...',
  },
  {
    label: 'Documents',
    match: (p) => p === '/documents/documentclassifications',
    resultsScope: 'documents-classifications',
    placeholder: 'Search classifications...',
  },
  {
    label: 'Vault',
    match: (p) =>
      p === '/credential-vault/credentialvaultlist' ||
      p === '/credential-vault/personalvaultlist',
    resultsScope: 'vault',
    placeholder: 'Search credentials...',
  },
  {
    label: 'Work Register',
    match: (p) => p === '/work-register/list' || p === '/work-register/team',
    resultsScope: 'work-register',
    placeholder: 'Search work register...',
  },
  {
    label: 'Users',
    match: (p) => p === '/settings/userlist',
    resultsScope: 'users',
    placeholder: 'Search users...',
  },
  {
    label: 'Staff',
    match: (p) => p === '/staff/stafflist',
    resultsScope: 'staff',
    placeholder: 'Search staff...',
  },
  {
    label: 'Vehicles',
    match: (p) => p === '/vehicle/vehiclelist',
    resultsScope: 'vehicles',
    placeholder: 'Search vehicles...',
  },
  {
    label: 'Branches',
    match: (p) => p === '/settings/branchlist',
    resultsScope: 'branches',
    placeholder: 'Search branches...',
  },
  {
    label: 'Departments',
    match: (p) => p === '/settings/departmentlist',
    resultsScope: 'departments',
    placeholder: 'Search departments...',
  },
  {
    label: 'Sub-Departments',
    match: (p) => p === '/settings/subdepartmentlist',
    resultsScope: 'sub-departments',
    placeholder: 'Search sub-departments...',
  },
  {
    label: 'Designations',
    match: (p) => p === '/settings/designationlist',
    resultsScope: 'designations',
    placeholder: 'Search designations...',
  },
  {
    label: 'Modules',
    match: (p) => p === '/modules',
    resultsScope: 'modules',
    placeholder: 'Search modules...',
  },
];

const SEARCH_HINTS = {
  projects: 'Type at least 2 characters to search projects and project tasks.',
  forum: 'Type at least 2 characters to search discussions.',
  documents: 'Type at least 2 characters to search documents.',
  'documents-shared': 'Type at least 2 characters to search shared documents.',
  'documents-favorites': 'Type at least 2 characters to search favorite documents.',
  'documents-classifications': 'Type at least 2 characters to search classifications.',
  tasks: 'Type at least 2 characters to search tasks.',
  worksheet: 'Type at least 2 characters to search worksheet tasks.',
  vault: 'Type at least 2 characters to search credentials.',
  'work-register': 'Type at least 2 characters to search work register entries.',
  users: 'Type at least 2 characters to search users.',
  staff: 'Type at least 2 characters to search staff.',
  vehicles: 'Type at least 2 characters to search vehicles.',
  branches: 'Type at least 2 characters to search branches.',
  departments: 'Type at least 2 characters to search departments.',
  'sub-departments': 'Type at least 2 characters to search sub-departments.',
  designations: 'Type at least 2 characters to search designations.',
  modules: 'Type at least 2 characters to search modules.',
};

/**
 * @param {string} pathname
 * @returns {{ show: boolean, mode?: 'global'|'module', label?: string, resultsScope?: string, placeholder?: string }}
 */
export function getHeaderSearchContext(pathname) {
  if (isDashboardPath(pathname)) {
    return {
      show: true,
      mode: 'global',
      label: 'all modules',
      placeholder: 'Global search across modules...',
    };
  }

  const normalized = normalizePathForComparison(pathname).toLowerCase();

  for (const item of MODULE_LIST_MATCHERS) {
    if (item.match(normalized)) {
      return {
        show: true,
        mode: 'module',
        label: item.label,
        placeholder: item.placeholder || `Search in ${item.label}...`,
        resultsScope: item.resultsScope,
      };
    }
  }

  return { show: false };
}

export function getHeaderSearchHint(searchContext) {
  if (!searchContext?.show) return '';
  if (searchContext.mode === 'global' || !searchContext.resultsScope) {
    return 'Type at least 2 characters to search all modules.';
  }
  return SEARCH_HINTS[searchContext.resultsScope] || `Type at least 2 characters to search in ${searchContext.label}...`;
}
